// ⌘K search palette (DESIGN §2): groups Batches · Clients (months included) · People · Pages, no filters.
// <SearchPalette/> is mounted once by the shell; openSearch() opens it; ⌘K / Ctrl K toggles it (ignored while a
// modal or drawer is open, so a half-typed form is never stacked over or discarded — A11Y-09).
// An empty query offers Add Batch (for the client/month in view) · New client and the pages.
// The input is a combobox: ↑/↓ move the active option, Enter opens it, Esc closes.
import './search.css';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import { monthLabel, pad2 } from '../../shared/constants.js';
import { search } from '../../shared/derive.js';
import { useStore } from '../store.js';
import { navigate, useRoute } from '../router.js';
import { Icon } from '../icons.jsx';
import { Avatar, StatusBadge, cx } from './ui.jsx';
import { openAddBatch, openAddClient } from './forms.jsx';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || '');
export const SEARCH_SHORTCUT = isMac ? '⌘K' : 'Ctrl K';

const PER_GROUP = 6;

// derive.search groups → palette groups (events last: a title/notes hit in the calendar still helps).
const GROUPS = [
  { key: 'batches', label: 'Batches' },
  { key: 'clients', label: 'Clients' },
  { key: 'people', label: 'People' },
  { key: 'pages', label: 'Pages' },
  { key: 'events', label: 'Events' },
];
const TYPE_ICON = { batch: 'folder', client: 'clients', month: 'calendar', event: 'clock' };

export const PAGE_LINKS = [
  { id: 'overview', title: 'Overview', href: '#/', icon: 'overview' },
  { id: 'workspace', title: 'Workspace', href: '#/workspace', icon: 'workspace' },
  { id: 'clients', title: 'Clients', href: '#/clients', icon: 'clients' },
  { id: 'calendar', title: 'Calendar', href: '#/calendar', icon: 'calendar' },
  { id: 'performance', title: 'Performance', href: '#/performance', icon: 'performance' },
  { id: 'people', title: 'People', href: '#/people', icon: 'people' },
  { id: 'settings', title: 'Settings', href: '#/settings', icon: 'settings' },
];

let setOpenFromOutside = null;

// True while a modal or drawer is on screen (their backdrops are the only full-screen layers below the palette).
const dialogOpen = () => typeof document !== 'undefined' && !!document.querySelector('.modal-backdrop, .drawer-backdrop');

export function openSearch() {
  if (!dialogOpen()) setOpenFromOutside?.(true);
}

export function closeSearch() {
  setOpenFromOutside?.(false);
}

export function SearchPalette() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    setOpenFromOutside = setOpen;
    const onKeyDown = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey || e.key.toLowerCase() !== 'k') return;
      e.preventDefault();
      setOpen((o) => (o ? false : !dialogOpen()));
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      setOpenFromOutside = null;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);
  return open ? <Palette onClose={() => setOpen(false)} /> : null;
}

// ---------- items ----------

// Batch hits lead with the stage — the answer people search for (VISUAL-16):
// "Northwind Coffee Co. · Month 04 · Batch 02 · Video Editing" → "Video Editing · Northwind Coffee Co. · Month 04 · Batch 02".
function stageFirst(subtitle = '') {
  const parts = subtitle.split(' · ');
  return parts.length > 1 ? [parts.pop(), ...parts].join(' · ') : subtitle;
}

// Fixed group order, except that a group whose top hit's title starts with the query moves first
// ("north" → the client Northwind before its batches; "sarah" → People first).
function resultItems(results, query) {
  const q = query.trim().toLowerCase();
  const leads = (g) => Number(!!q && String(results[g.key]?.[0]?.title || '').toLowerCase().startsWith(q));
  const order = [...GROUPS].sort((a, b) => leads(b) - leads(a));
  return order.map((g) => ({
    key: g.key,
    label: g.label,
    items: (results[g.key] || []).map((r) => ({
      key: `${r.type}:${r.id}`,
      type: r.type,
      icon: r.type === 'page' ? PAGE_LINKS.find((p) => p.id === r.id)?.icon || 'arrowRight' : TYPE_ICON[r.type] || 'search',
      title: r.title,
      subtitle: r.type === 'batch' ? (r.stage ? `${r.stage} · ${r.context}` : stageFirst(r.subtitle)) : r.subtitle === 'Page' ? '' : r.subtitle,
      status: r.type === 'batch' ? r.status : '',
      href: r.href,
    })),
  })).filter((g) => g.items.length);
}

// The client (+ month) in view, for a contextual Add Batch.
function batchContext(route, index) {
  if (route.name === 'workspace' && route.params.clientId && index.clientsById.has(route.params.clientId)) {
    return { clientId: route.params.clientId, monthNumber: route.params.monthNumber || 0 };
  }
  if (route.name === 'batch') {
    const batch = index.batchesById.get(route.params.batchId);
    if (batch) return { clientId: batch.clientId, monthNumber: index.monthsById.get(batch.monthId)?.monthNumber || 0 };
  }
  return null;
}

function emptyQueryItems(index, route) {
  const here = batchContext(route, index);
  const client = here && index.clientsById.get(here.clientId);
  const where = client ? [client.name, here.monthNumber ? monthLabel(here.monthNumber) : ''].filter(Boolean).join(' · ') : '';
  return [
    {
      key: 'actions',
      label: 'Actions',
      // No clients yet → Add Client first (a batch needs a client).
      items: [
        { key: 'add-batch', icon: 'plus', title: 'Add Batch', subtitle: where, run: () => openAddBatch(here || {}) },
        { key: 'add-client', icon: 'plus', title: 'Add Client', subtitle: '', run: () => openAddClient() },
      ][index.clientsById.size ? 'slice' : 'reverse'](),
    },
    { key: 'pages', label: 'Pages', items: PAGE_LINKS.map((p) => ({ key: `page:${p.id}`, icon: p.icon, title: p.title, href: p.href })) },
  ];
}

// ---------- highlighting ----------

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Words to highlight: the plain query tokens, plus "Month 03" / "Batch 04" for number filters (mirrors derive's parser).
function highlightTerms(query) {
  let rest = String(query || '').toLowerCase();
  const terms = [];
  rest = rest.replace(/\bmonth\s*0*(\d{1,2})\b/, (_, n) => { terms.push(`month ${pad2(n)}`); return ' '; });
  rest = rest.replace(/\bbatch\s*0*(\d{1,3})\b/, (_, n) => { terms.push(`batch ${pad2(n)}`); return ' '; });
  return [...terms, ...rest.split(/\s+/).filter(Boolean)].sort((a, b) => b.length - a.length);
}

function highlight(text, terms) {
  if (!text || !terms.length) return text;
  const parts = String(text).split(new RegExp(`(${terms.map(escapeRe).join('|')})`, 'gi'));
  return parts.map((part, i) => (i % 2 ? <mark key={i} class="pal-mark">{part}</mark> : part));
}

// ---------- palette ----------

const LIST_ID = 'pal-list';
const optionId = (i) => `pal-opt-${i}`;

function Palette({ onClose }) {
  const { state, index, today } = useStore();
  const route = useRoute();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const keyboardMove = useRef(false);
  const q = query.trim();

  const groups = useMemo(() => {
    if (!state || !index) return [];
    if (!q) return emptyQueryItems(index, route);
    return resultItems(search(state, index, query, today, PER_GROUP), query);
  }, [state, index, query, today, route]);
  const items = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  const terms = useMemo(() => (q ? highlightTerms(query) : []), [query]);
  const current = Math.min(active, Math.max(items.length - 1, 0));

  // Focus the input first (before the next keystroke lands on the page) and lock the page scroll.
  useLayoutEffect(() => {
    const previous = document.activeElement;
    const body = document.body.style;
    const overflow = body.overflow;
    body.overflow = 'hidden';
    inputRef.current?.focus();
    return () => {
      body.overflow = overflow;
      // Give focus back, unless something else (e.g. a sheet a quick action opened) has already taken it.
      const now = document.activeElement;
      const ours = !now || now === document.body || rootRef.current?.contains(now);
      if (ours && previous && previous.isConnected && typeof previous.focus === 'function') previous.focus({ preventScroll: true });
    };
  }, []);

  // Keep the keyboard selection visible; moving to the first option also reveals its group label.
  useEffect(() => {
    if (!keyboardMove.current) return;
    keyboardMove.current = false;
    if (current === 0) {
      if (listRef.current) listRef.current.scrollTop = 0;
      return;
    }
    document.getElementById(optionId(current))?.scrollIntoView({ block: 'nearest' });
  }, [current]);

  const activate = (item) => {
    if (!item) return;
    onClose();
    if (item.href) navigate(item.href.replace(/^#/, ''));
    // After the palette has unmounted, so the opened sheet keeps focus and the scroll lock.
    if (item.run) setTimeout(item.run, 0);
  };

  const move = (delta) => {
    if (!items.length) return;
    keyboardMove.current = true;
    setActive((current + delta + items.length) % items.length);
  };

  const onKeyDown = (e) => {
    if (e.isComposing) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    } else if (e.key === 'ArrowDown' || (e.ctrlKey && e.key === 'n')) {
      e.preventDefault();
      move(1);
    } else if (e.key === 'ArrowUp' || (e.ctrlKey && e.key === 'p')) {
      e.preventDefault();
      move(-1);
    } else if (e.key === 'Enter' && e.target === inputRef.current) {
      e.preventDefault();
      activate(items[current]);
    } else if (e.key === 'Tab') {
      // Focus stays inside the palette: the input and the close button.
      const stops = [...rootRef.current.querySelectorAll('input, button:not([tabindex="-1"])')].filter((el) => el.offsetParent !== null);
      if (!stops.length) return;
      e.preventDefault();
      const i = stops.indexOf(document.activeElement);
      stops[(i + (e.shiftKey ? -1 : 1) + stops.length) % stops.length].focus();
    }
  };

  const onBackdropDown = (e) => { e.currentTarget.dataset.down = e.target === e.currentTarget ? '1' : ''; };
  const onBackdropClick = (e) => {
    if (e.target === e.currentTarget && e.currentTarget.dataset.down === '1') onClose();
  };

  let n = -1;
  return (
    <div class="pal-backdrop" onMouseDown={onBackdropDown} onClick={onBackdropClick}>
      <div ref={rootRef} class="sp" role="dialog" aria-modal="true" aria-label="Search" onKeyDown={onKeyDown}>
        <div class="pal-head">
          <Icon name="search" size={17} class="pal-head-icon" />
          <input
            ref={inputRef}
            class="pal-input"
            type="text"
            role="combobox"
            aria-label="Search"
            aria-expanded={items.length > 0 ? 'true' : 'false'}
            aria-controls={LIST_ID}
            aria-autocomplete="list"
            aria-activedescendant={items.length ? optionId(current) : undefined}
            placeholder="Search batches, clients, people…"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellcheck={false}
            enterKeyHint="go"
            value={query}
            onInput={(e) => { setQuery(e.currentTarget.value); setActive(0); }}
          />
          <button type="button" class="pal-esc" onClick={onClose} aria-label="Close search">Esc</button>
          <button type="button" class="btn btn-ghost btn-sm pal-cancel" onClick={onClose}>Cancel</button>
        </div>

        {items.length > 0 && (
          <div ref={listRef} id={LIST_ID} class="pal-list" role="listbox" aria-label={q ? 'Search results' : 'Suggestions'}>
            {groups.map((g) => (
              <div key={g.key} class="pal-group" role="group" aria-labelledby={`pal-group-${g.key}`}>
                <div class="pal-group-label" id={`pal-group-${g.key}`} role="presentation">{g.label}</div>
                {g.items.map((item) => {
                  const i = ++n;
                  const selected = i === current;
                  return (
                    <button
                      key={item.key}
                      id={optionId(i)}
                      type="button"
                      role="option"
                      tabIndex={-1}
                      aria-selected={selected}
                      class={cx('pal-item', selected && 'is-active')}
                      // pointermove, not mouseenter: results rendering under a resting pointer fire mouseenter,
                      // which would steal the keyboard selection and make Enter open the wrong result.
                      onPointerMove={() => { if (i !== current) setActive(i); }}
                      onMouseDown={(e) => e.preventDefault()} // keep focus (and the caret) in the input
                      onClick={() => activate(item)}
                    >
                      {item.type === 'person'
                        ? <Avatar name={item.title} size={24} class="pal-avatar" />
                        : <span class="pal-icon" aria-hidden="true"><Icon name={item.icon} size={15} /></span>}
                      <span class="pal-text">
                        <span class="pal-title">{highlight(item.title, terms)}</span>
                        {item.subtitle && <span class="pal-sub">{item.subtitle}</span>}
                      </span>
                      {item.status && <StatusBadge status={item.status} kind="batch" class="pal-status" />}
                      <span class="pal-enter" aria-hidden="true">↵</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {q && !items.length && (
          <div class="pal-empty" role="status">
            <p class="pal-empty-title">No results for “{q}”</p>
            <p class="pal-empty-desc">Try a batch, client, month, stage or person.</p>
          </div>
        )}

        <div class="pal-foot" aria-hidden="true">
          <span class="pal-keys"><span class="kbd">↑</span><span class="kbd">↓</span>Move</span>
          <span class="pal-keys"><span class="kbd">↵</span>Open</span>
          <span class="pal-keys"><span class="kbd">Esc</span>Close</span>
        </div>
      </div>
    </div>
  );
}
